import React, { useState, useEffect, useCallback } from 'react';
import { View, Text, TouchableOpacity, TextInput, ScrollView, ActivityIndicator, Platform } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import Constants from 'expo-constants';
import { db, getAdminAuthHeaders } from '../firebaseConfig';
import { DEFAULT_PUBLIC_CATALOG, formatPrice, yearlySavings } from '../utils/billingPlans';
import { setupStripeCatalog } from '../services/stripeService';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getApp } from 'firebase/app';
import { doc, onSnapshot, setDoc } from 'firebase/firestore';
import { uiVar } from './floodlight/uiStyleCss';

const getBase = () => (
  (typeof process !== 'undefined' && process?.env?.EXPO_PUBLIC_FUNCTIONS_BASE_URL)
  || (Constants?.expoConfig?.extra?.EXPO_PUBLIC_FUNCTIONS_BASE_URL)
  || (Constants?.expoConfig?.extra?.FUNCTIONS_BASE_URL)
  || (Constants?.manifest?.extra?.FUNCTIONS_BASE_URL)
);

const PLAN_COLORS = {
  FREE: { bg: 'rgba(107,114,128,0.12)', border: 'rgba(107,114,128,0.3)', accent: '#9CA3AF', label: 'Free' },
  BASIC: { bg: 'rgba(59,130,246,0.10)', border: 'rgba(59,130,246,0.3)', accent: '#3B82F6', label: 'Basic' },
  PRO: { bg: 'rgba(168,85,247,0.10)', border: 'rgba(168,85,247,0.3)', accent: '#A855F7', label: 'Pro' },
  ULTIMATE: { bg: 'rgba(245,158,11,0.10)', border: 'rgba(245,158,11,0.3)', accent: '#F59E0B', label: 'Ultimate' },
};

const USER_TYPE_COLORS = {
  admin: { bg: 'rgba(239,68,68,0.10)', border: 'rgba(239,68,68,0.3)', accent: '#EF4444' },
  user: { bg: 'rgba(16,185,129,0.10)', border: 'rgba(16,185,129,0.3)', accent: '#10B981' },
};

// Read-only fallback when getSubscriptionConfig is unreachable. Saving is
// disabled in that state so defaults can never overwrite the real config.
const LOCAL_DEFAULTS = {
  plans: Object.fromEntries(Object.entries(DEFAULT_PUBLIC_CATALOG.plans).map(([k, p]) => [k, {
    ...p, prices: { month: { amount: p.prices.month.amount }, year: { amount: p.prices.year.amount } },
  }])),
  userTypes: {
    admin: { name: 'Admin', queryLimit: -1, backgroundQueryLimit: 10, description: 'Full access, unlimited everything' },
    user: { name: 'User', inheritsFromPlan: true, description: 'Limits determined by subscription plan' },
  },
};

// ── Stripe catalog (products + monthly/yearly prices) ────────────────────────
function StripeCatalogSection({ onApplied }) {
  const [busy, setBusy] = useState(null);
  const [report, setReport] = useState(null);
  const [err, setErr] = useState(null);

  const run = async (mode, apply) => {
    if (apply && typeof window !== 'undefined' && !window.confirm(`Create any missing ${mode.toUpperCase()} Stripe products and prices at the amounts saved below? Existing prices are never edited.`)) return;
    setBusy(`${mode}:${apply}`); setErr(null);
    try {
      const r = await setupStripeCatalog({ mode, apply });
      setReport(r);
      if (apply) onApplied?.();
    } catch (e) { setErr(e.message); } finally { setBusy(null); }
  };

  const Btn = ({ mode, apply, label }) => (
    <TouchableOpacity style={[sm.toggle, { paddingHorizontal: 12, borderColor: apply ? 'rgba(16,185,129,0.5)' : 'rgba(255,255,255,0.15)' }]} disabled={!!busy} onPress={() => run(mode, apply)} accessibilityRole="button" accessibilityLabel={label}>
      {busy === `${mode}:${apply}` ? <ActivityIndicator size="small" color="#A855F7" /> : <Text style={{ color: apply ? '#10B981' : 'rgba(255,255,255,0.8)', fontWeight: '700', fontSize: 12 }}>{label}</Text>}
    </TouchableOpacity>
  );

  return (
    <View style={sm.card}>
      <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6, flexWrap: 'wrap', gap: 8 }}>
        <Text style={sm.cardTitle}>Stripe catalog</Text>
        <View style={{ flexDirection: 'row', gap: 8, flexWrap: 'wrap' }}>
          <Btn mode="live" apply={false} label="Check live" />
          <Btn mode="live" apply label="Create live prices" />
          <Btn mode="test" apply={false} label="Check test" />
          <Btn mode="test" apply label="Create test prices" />
        </View>
      </View>
      <Text style={{ color: 'rgba(255,255,255,0.55)', fontSize: 12, marginBottom: 8 }}>
        Creates one product per paid plan and a monthly + yearly price at the amounts below (lookup keys bo_plan_monthly / bo_plan_yearly). A changed amount creates a new price for new subscribers; existing subscribers keep theirs.
      </Text>
      {err && <Text style={{ color: '#EF4444', fontSize: 12 }}>{err}</Text>}
      {report && (
        <View style={{ gap: 4 }}>
          <Text style={{ color: report.livemode ? '#10B981' : '#F59E0B', fontSize: 12, fontWeight: '700' }}>{report.livemode ? 'LIVE' : 'TEST'} · {report.created?.length ? `created ${report.created.length}` : 'nothing created'}</Text>
          {Object.entries(report.plans || {}).map(([k, v]) => (
            <Text key={k} style={{ color: 'rgba(255,255,255,0.75)', fontSize: 12, fontFamily: Platform.OS === 'web' ? 'ui-monospace, Menlo, monospace' : undefined }}>
              {k}: month {v.month?.priceId ? `✓ $${v.month.amount}` : `missing ($${v.month?.amount})`} · year {v.year?.priceId ? `✓ $${v.year.amount}` : `missing ($${v.year?.amount})`}
            </Text>
          ))}
          <Text style={{ color: report.webhook?.length ? 'rgba(255,255,255,0.75)' : '#EF4444', fontSize: 12 }}>
            Webhook: {report.webhook?.length ? report.webhook.map((w) => `${w.status} (${(w.events || []).length} events)`).join(', ') : 'no endpoint points at stripeWebhook in this mode'}
          </Text>
        </View>
      )}
    </View>
  );
}

// ── Stripe Mode Toggles ───────────────────────────────────────────────────────
function StripeModeSection() {
  const [liveDev, setLiveDev] = useState(false);
  const [liveStg, setLiveStg] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const unsub = onSnapshot(
      doc(db, 'site_config', 'stripe_settings'),
      (snap) => {
        const d = snap.exists() ? snap.data() : {};
        setLiveDev(d.useLiveInDevelopment === true);
        setLiveStg(d.useLiveInStaging === true);
        setLoading(false);
      },
      () => setLoading(false)
    );
    return () => unsub();
  }, []);

  const toggle = async (field, next) => {
    setSaving(true);
    try {
      await setDoc(doc(db, 'site_config', 'stripe_settings'), { [field]: next }, { merge: true });
    } catch (e) {
      console.error('[StripeModeSection] save failed:', e.message);
    } finally {
      setSaving(false);
    }
  };

  const ToggleRow = ({ label, sublabel, value, field }) => (
    <View style={sm.row}>
      <View style={{ flex: 1 }}>
        <Text style={sm.rowLabel}>{label}</Text>
        <Text style={sm.rowSub}>{sublabel}</Text>
      </View>
      <TouchableOpacity
        onPress={loading || saving ? undefined : () => toggle(field, !value)}
        activeOpacity={0.8}
        style={[sm.track, value ? sm.trackOn : sm.trackOff]}
      >
        {(loading || saving)
          ? <ActivityIndicator size="small" color="#fff" style={{ margin: 4 }} />
          : <View style={[sm.thumb, value ? sm.thumbOn : sm.thumbOff]} />
        }
      </TouchableOpacity>
    </View>
  );

  return (
    <View style={sm.card}>
      <View style={sm.cardHeader}>
        <Ionicons name="logo-usd" size={16} color="#F59E0B" style={{ marginRight: 7 }} />
        <Text style={sm.cardTitle}>Stripe mode</Text>
        <View style={sm.badge}>
          <Text style={sm.badgeText}>Production is always live</Text>
        </View>
      </View>
      <Text style={sm.desc}>
        Production always uses live Stripe keys. Sandbox and staging default to test mode but can be switched on below if needed.
      </Text>
      <ToggleRow
        label="Use live Stripe on localhost (dev)"
        sublabel="Off = sandbox. Only enable for debugging real billing from your machine."
        value={liveDev}
        field="useLiveInDevelopment"
      />
      <ToggleRow
        label="Use live Stripe on staging"
        sublabel="Off = sandbox (default). Enable only to validate end-to-end on staging."
        value={liveStg}
        field="useLiveInStaging"
      />
    </View>
  );
}

// ── Pricing Experiments ───────────────────────────────────────────────────────
// Controls A/B price testing, auto-applied discounts, and promo code availability.
// Backed by Firestore: site_config/pricing_experiments
// The createCheckoutSession Cloud Function reads this config at session creation
// time so changes take effect immediately — no deploy required.
const EXP_PLANS = ['BASIC', 'PRO', 'ULTIMATE'];

function PricingExperimentsSection() {
  const [exp, setExp] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveOk, setSaveOk] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    const unsub = onSnapshot(
      doc(db, 'site_config', 'pricing_experiments'),
      (snap) => setExp(snap.exists() ? snap.data() : { active: false, promo_codes_enabled: true, variant_b_distribution: 20, plans: {} }),
      () => setExp({ active: false, promo_codes_enabled: true, variant_b_distribution: 20, plans: {} })
    );
    return () => unsub();
  }, []);

  const save = async (next) => {
    setSaving(true);
    try {
      await setDoc(doc(db, 'site_config', 'pricing_experiments'), next, { merge: true });
      setSaveOk(true);
      setTimeout(() => setSaveOk(false), 2000);
    } catch (e) {
      console.error('[PricingExperimentsSection] save failed:', e.message);
    } finally {
      setSaving(false);
    }
  };

  const patch = (partial) => {
    const next = { ...exp, ...partial };
    setExp(next);
    save(next);
  };

  const patchPlan = (planKey, partial) => {
    const next = { ...exp, plans: { ...(exp.plans || {}), [planKey]: { ...(exp.plans?.[planKey] || {}), ...partial } } };
    setExp(next);
    save(next);
  };

  if (!exp) return (
    <View style={[pe.card, { justifyContent: 'center', alignItems: 'center', padding: 24 }]}>
      <ActivityIndicator size="small" color="#10B981" />
    </View>
  );

  const dist = exp.variant_b_distribution ?? 20;

  return (
    <View style={pe.card}>
      {/* Header row */}
      <TouchableOpacity style={pe.headerRow} onPress={() => setExpanded(e => !e)} activeOpacity={0.8}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, flex: 1 }}>
          <Ionicons name="flask" size={16} color="#10B981" />
          <Text style={pe.title}>Pricing experiments</Text>
          {exp.active && (
            <View style={pe.activePill}><Text style={pe.activePillText}>LIVE</Text></View>
          )}
          {saveOk && <Ionicons name="checkmark-circle" size={14} color="#10B981" />}
          {saving && <ActivityIndicator size="small" color="#10B981" />}
        </View>
        <Ionicons name={expanded ? 'chevron-up' : 'chevron-down'} size={16} color="rgba(255,255,255,0.35)" />
      </TouchableOpacity>

      <Text style={pe.desc}>
        A/B test prices, auto-apply coupons, and control promo code access — all without a code deploy.
        {!exp.active && ' Toggle ON below to activate.'}
      </Text>

      {/* Top controls — always visible */}
      <View style={pe.topControls}>
        <View style={pe.controlItem}>
          <Text style={pe.controlLabel}>Experiments active</Text>
          <TouchableOpacity
            style={[sm.track, exp.active ? sm.trackOn : sm.trackOff]}
            onPress={() => patch({ active: !exp.active })} activeOpacity={0.8}
          >
            <View style={[sm.thumb, exp.active ? sm.thumbOn : sm.thumbOff]} />
          </TouchableOpacity>
        </View>
        <View style={pe.controlItem}>
          <Text style={pe.controlLabel}>Promo codes</Text>
          <TouchableOpacity
            style={[sm.track, exp.promo_codes_enabled !== false ? sm.trackOn : sm.trackOff]}
            onPress={() => patch({ promo_codes_enabled: exp.promo_codes_enabled === false })} activeOpacity={0.8}
          >
            <View style={[sm.thumb, exp.promo_codes_enabled !== false ? sm.thumbOn : sm.thumbOff]} />
          </TouchableOpacity>
        </View>
        <View style={[pe.controlItem, { flex: 1 }]}>
          <Text style={pe.controlLabel}>Variant B split: <Text style={{ color: '#10B981', fontWeight: '700' }}>{dist}%</Text></Text>
          <View style={{ flexDirection: 'row', gap: 6, marginTop: 4 }}>
            {[10, 20, 30, 50].map(pct => (
              <TouchableOpacity key={pct}
                style={[pe.pctBtn, dist === pct && pe.pctBtnActive]}
                onPress={() => patch({ variant_b_distribution: pct })}
              >
                <Text style={[pe.pctBtnText, dist === pct && pe.pctBtnTextActive]}>{pct}%</Text>
              </TouchableOpacity>
            ))}
          </View>
        </View>
      </View>

      {/* Per-plan variant B config */}
      {expanded && (
        <View style={pe.planGrid}>
          <View style={pe.planGridHeader}>
            <Text style={pe.planGridHeaderText}>Plan</Text>
            <Text style={pe.planGridHeaderText}>Variant B price ID (test)</Text>
            <Text style={pe.planGridHeaderText}>Variant B price ID (live)</Text>
            <Text style={pe.planGridHeaderText}>Auto-coupon (test)</Text>
          </View>
          {EXP_PLANS.map(planKey => {
            const planExp = exp.plans?.[planKey] || {};
            return (
              <View key={planKey} style={pe.planGridRow}>
                <View style={pe.planKeyCell}>
                  <View style={[pe.planPill, { backgroundColor: PLAN_COLORS[planKey]?.accent + '25' }]}>
                    <Text style={[pe.planPillText, { color: PLAN_COLORS[planKey]?.accent }]}>{planKey}</Text>
                  </View>
                </View>
                <TextInput
                  style={pe.cellInput}
                  value={planExp.price_id_test_b || ''}
                  onChangeText={v => patchPlan(planKey, { price_id_test_b: v || '' })}
                  placeholder="price_xxx"
                  placeholderTextColor="rgba(255,255,255,0.18)"
                />
                <TextInput
                  style={pe.cellInput}
                  value={planExp.price_id_live_b || ''}
                  onChangeText={v => patchPlan(planKey, { price_id_live_b: v || '' })}
                  placeholder="price_xxx"
                  placeholderTextColor="rgba(255,255,255,0.18)"
                />
                <TextInput
                  style={pe.cellInput}
                  value={planExp.active_coupon_test || ''}
                  onChangeText={v => patchPlan(planKey, { active_coupon_test: v || '' })}
                  placeholder="e.g. LAUNCH20"
                  placeholderTextColor="rgba(255,255,255,0.18)"
                />
              </View>
            );
          })}
          <Text style={pe.footnote}>
            Variant A always uses the main plan price IDs from Subscription Plans above.
            Variant B assignment is sticky per user (UID hash), so the same user always sees the same price.
            Auto-coupon is applied silently at checkout — use for launch discounts or targeted offers.
          </Text>
        </View>
      )}
    </View>
  );
}

const pe = {
  card: { backgroundColor: 'rgba(16,185,129,0.06)', borderWidth: 1, borderColor: 'rgba(16,185,129,0.2)', borderRadius: 12, padding: 16, marginBottom: 24 },
  headerRow: { flexDirection: 'row', alignItems: 'center', marginBottom: 8 },
  title: { fontSize: 14, fontWeight: '700', color: '#10B981' },
  activePill: { backgroundColor: 'rgba(16,185,129,0.2)', borderRadius: 5, paddingHorizontal: 7, paddingVertical: 2 },
  activePillText: { fontSize: 10, color: '#10B981', fontWeight: '800', letterSpacing: 0.5 },
  desc: { fontSize: 12, color: 'rgba(255,255,255,0.45)', marginBottom: 14, lineHeight: 18 },
  topControls: { flexDirection: 'row', gap: 20, flexWrap: 'wrap', borderTopWidth: 1, borderTopColor: 'rgba(255,255,255,0.06)', paddingTop: 12 },
  controlItem: { flexDirection: 'column', gap: 8 },
  controlLabel: { fontSize: 12, color: 'rgba(255,255,255,0.6)', fontWeight: '600' },
  pctBtn: { paddingHorizontal: 12, paddingVertical: 5, borderRadius: 7, borderWidth: 1, borderColor: 'rgba(255,255,255,0.12)', backgroundColor: 'rgba(255,255,255,0.04)' },
  pctBtnActive: { borderColor: '#10B981', backgroundColor: 'rgba(16,185,129,0.15)' },
  pctBtnText: { fontSize: 12, color: 'rgba(255,255,255,0.5)', fontWeight: '600' },
  pctBtnTextActive: { color: '#10B981' },
  planGrid: { marginTop: 16, borderTopWidth: 1, borderTopColor: 'rgba(255,255,255,0.06)', paddingTop: 12 },
  planGridHeader: { flexDirection: 'row', gap: 8, marginBottom: 8, paddingHorizontal: 4 },
  planGridHeaderText: { flex: 1, fontSize: 10, color: 'rgba(255,255,255,0.35)', fontWeight: '700', textTransform: 'uppercase', letterSpacing: 0.5 },
  planGridRow: { flexDirection: 'row', gap: 8, marginBottom: 8, alignItems: 'center' },
  planKeyCell: { flex: 1, alignItems: 'flex-start' },
  planPill: { paddingHorizontal: 8, paddingVertical: 4, borderRadius: 6 },
  planPillText: { fontSize: 11, fontWeight: '800', letterSpacing: 0.4 },
  cellInput: {
    flex: 1, backgroundColor: 'rgba(255,255,255,0.05)', borderRadius: 7, borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.08)', paddingHorizontal: 8, paddingVertical: 6,
    color: '#fff', fontSize: 11,
  },
  footnote: { fontSize: 11, color: 'rgba(255,255,255,0.3)', lineHeight: 17, marginTop: 8, fontStyle: 'italic' },
};

const sm = {
  card: { backgroundColor: 'rgba(245,158,11,0.07)', borderWidth: 1, borderColor: 'rgba(245,158,11,0.2)', borderRadius: 12, padding: 16, marginBottom: 24 },
  cardHeader: { flexDirection: 'row', alignItems: 'center', marginBottom: 8 },
  cardTitle: { fontSize: 14, fontWeight: '700', color: '#F59E0B' },
  badge: { marginLeft: 'auto', backgroundColor: 'rgba(16,185,129,0.15)', borderRadius: 6, paddingHorizontal: 8, paddingVertical: 2 },
  badgeText: { fontSize: 11, color: '#10B981', fontWeight: '600' },
  desc: { fontSize: 12, color: 'rgba(255,255,255,0.5)', marginBottom: 14, lineHeight: 18 },
  row: { flexDirection: 'row', alignItems: 'center', paddingVertical: 10, borderTopWidth: 1, borderTopColor: 'rgba(255,255,255,0.06)' },
  rowLabel: { fontSize: 13, color: 'rgba(255,255,255,0.85)', fontWeight: '600', marginBottom: 2 },
  rowSub: { fontSize: 11, color: 'rgba(255,255,255,0.4)', lineHeight: 16 },
  track: { width: 44, height: 26, borderRadius: 13, justifyContent: 'center', paddingHorizontal: 3, marginLeft: 12 },
  trackOn: { backgroundColor: '#F59E0B' },
  trackOff: { backgroundColor: 'rgba(255,255,255,0.15)' },
  thumb: { width: 20, height: 20, borderRadius: 10, backgroundColor: '#fff' },
  thumbOn: { alignSelf: 'flex-end' },
  thumbOff: { alignSelf: 'flex-start' },
};

// ── Coupons ───────────────────────────────────────────────────────────────────
// Lists, creates, and archives Stripe coupons. Changes take effect immediately
// in Stripe (no config doc needed — direct Stripe API calls via Cloud Function).

const DURATION_OPTIONS = [
  { value: 'once', label: 'Once', hint: 'Applied to first invoice only' },
  { value: 'repeating', label: 'Repeating', hint: 'Applied for N months' },
  { value: 'forever', label: 'Forever', hint: 'Applied to all invoices' },
];

const EMPTY_FORM = {
  name: '', couponId: '', discountType: 'percent', percentOff: '20',
  amountOff: '10', duration: 'once', durationInMonths: '3',
  maxRedemptions: '', redeemBy: '',
};

function CouponsSection() {
  const [coupons, setCoupons] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [archiving, setArchiving] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState(null);
  const [createOk, setCreateOk] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const _fn = (name) => httpsCallable(getFunctions(getApp()), name);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await _fn('listStripeCoupons')();
      setCoupons(res.data?.coupons || []);
    } catch (e) {
      setError(e.message || 'Failed to load coupons');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleArchive = async (id) => {
    if (!window.confirm(`Archive coupon "${id}"? This cannot be undone.`)) return;
    setArchiving(id);
    try {
      await _fn('archiveStripeCoupon')({ couponId: id });
      setCoupons(prev => prev.filter(c => c.id !== id));
    } catch (e) {
      alert(`Failed to archive: ${e.message}`);
    } finally {
      setArchiving(null);
    }
  };

  const handleCreate = async () => {
    setCreating(true);
    setCreateError(null);
    setCreateOk(false);
    try {
      const payload = {
        duration: form.duration,
        name: form.name || undefined,
        couponId: form.couponId || undefined,
        maxRedemptions: form.maxRedemptions ? parseInt(form.maxRedemptions, 10) : undefined,
        redeemBy: form.redeemBy || undefined,
      };
      if (form.discountType === 'percent') {
        payload.percentOff = parseFloat(form.percentOff);
      } else {
        payload.amountOff = parseFloat(form.amountOff);
      }
      if (form.duration === 'repeating') {
        payload.durationInMonths = parseInt(form.durationInMonths, 10);
      }
      const res = await _fn('createStripeCoupon')(payload);
      const newCoupon = res.data?.coupon;
      if (newCoupon) setCoupons(prev => [newCoupon, ...(prev || [])]);
      setForm(EMPTY_FORM);
      setShowCreate(false);
      setCreateOk(true);
      setTimeout(() => setCreateOk(false), 3000);
    } catch (e) {
      setCreateError(e.message || 'Failed to create coupon');
    } finally {
      setCreating(false);
    }
  };

  const pf = (field) => (v) => setForm(prev => ({ ...prev, [field]: v }));

  const formatDiscount = (c) => {
    if (c.percent_off) return `${c.percent_off}% off`;
    if (c.amount_off) return `$${(c.amount_off / 100).toFixed(2)} off`;
    return '—';
  };
  const formatDuration = (c) => {
    if (c.duration === 'repeating') return `${c.duration_in_months}mo`;
    return c.duration;
  };

  return (
    <View style={cp.card}>
      <TouchableOpacity style={cp.headerRow} onPress={() => setExpanded(e => !e)} activeOpacity={0.8}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, flex: 1 }}>
          <Ionicons name="pricetag" size={16} color="#EC4899" />
          <Text style={cp.title}>Coupons</Text>
          {coupons && (
            <View style={cp.countPill}>
              <Text style={cp.countPillText}>{coupons.filter(c => c.valid).length} active</Text>
            </View>
          )}
          {createOk && <Ionicons name="checkmark-circle" size={14} color="#10B981" />}
        </View>
        <Ionicons name={expanded ? 'chevron-up' : 'chevron-down'} size={16} color="rgba(255,255,255,0.35)" />
      </TouchableOpacity>

      <Text style={cp.desc}>
        Manage Stripe discount coupons for subscription checkout. Coupons can be auto-applied via Pricing Experiments above, or entered manually by users as promo codes.
      </Text>

      {expanded && (
        <View style={{ marginTop: 4 }}>
          {/* Create button */}
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <TouchableOpacity style={cp.refreshBtn} onPress={load} disabled={loading}>
              <Ionicons name="refresh" size={13} color="rgba(255,255,255,0.5)" />
              <Text style={cp.refreshBtnText}>Refresh</Text>
            </TouchableOpacity>
            <TouchableOpacity style={cp.createBtn} onPress={() => { setShowCreate(s => !s); setCreateError(null); }} activeOpacity={0.8}>
              <Ionicons name={showCreate ? 'close' : 'add'} size={14} color="#EC4899" />
              <Text style={cp.createBtnText}>{showCreate ? 'Cancel' : 'New Coupon'}</Text>
            </TouchableOpacity>
          </View>

          {/* Create form */}
          {showCreate && (
            <View style={cp.createForm}>
              <Text style={cp.formTitle}>New Coupon</Text>

              <View style={cp.formRow}>
                <View style={{ flex: 1 }}>
                  <Text style={cp.formLabel}>Name (shown to customer)</Text>
                  <TextInput style={cp.formInput} value={form.name} onChangeText={pf('name')} placeholder="e.g. Launch 20% off" placeholderTextColor="rgba(255,255,255,0.2)" />
                </View>
                <View style={{ flex: 1 }}>
                  <Text style={cp.formLabel}>Coupon ID (optional, auto-generated if blank)</Text>
                  <TextInput style={cp.formInput} value={form.couponId} onChangeText={pf('couponId')} placeholder="e.g. LAUNCH20" placeholderTextColor="rgba(255,255,255,0.2)" autoCapitalize="characters" />
                </View>
              </View>

              {/* Discount type toggle */}
              <View style={cp.formRow}>
                <View style={{ flex: 1 }}>
                  <Text style={cp.formLabel}>Discount type</Text>
                  <View style={{ flexDirection: 'row', gap: 8, marginTop: 4 }}>
                    {[{ v: 'percent', label: '% Off' }, { v: 'fixed', label: '$ Off' }].map(({ v, label }) => (
                      <TouchableOpacity key={v}
                        style={[cp.typeBtn, form.discountType === v && cp.typeBtnActive]}
                        onPress={() => setForm(prev => ({ ...prev, discountType: v }))}>
                        <Text style={[cp.typeBtnText, form.discountType === v && cp.typeBtnTextActive]}>{label}</Text>
                      </TouchableOpacity>
                    ))}
                  </View>
                </View>
                <View style={{ flex: 1 }}>
                  {form.discountType === 'percent' ? (
                    <>
                      <Text style={cp.formLabel}>Percent off (1–100)</Text>
                      <TextInput style={cp.formInput} value={form.percentOff} onChangeText={pf('percentOff')} keyboardType="numeric" placeholder="20" placeholderTextColor="rgba(255,255,255,0.2)" />
                    </>
                  ) : (
                    <>
                      <Text style={cp.formLabel}>Amount off (USD)</Text>
                      <TextInput style={cp.formInput} value={form.amountOff} onChangeText={pf('amountOff')} keyboardType="numeric" placeholder="10.00" placeholderTextColor="rgba(255,255,255,0.2)" />
                    </>
                  )}
                </View>
              </View>

              {/* Duration */}
              <View style={{ marginBottom: 12 }}>
                <Text style={cp.formLabel}>Duration</Text>
                <View style={{ flexDirection: 'row', gap: 8, marginTop: 4, flexWrap: 'wrap' }}>
                  {DURATION_OPTIONS.map(({ value, label, hint }) => (
                    <TouchableOpacity key={value}
                      style={[cp.typeBtn, form.duration === value && cp.typeBtnActive, { flex: 1, minWidth: 90 }]}
                      onPress={() => setForm(prev => ({ ...prev, duration: value }))}>
                      <Text style={[cp.typeBtnText, form.duration === value && cp.typeBtnTextActive]}>{label}</Text>
                      <Text style={[cp.typeHint, form.duration === value && { color: 'rgba(236,72,153,0.7)' }]}>{hint}</Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </View>

              {form.duration === 'repeating' && (
                <View style={cp.formRow}>
                  <View style={{ flex: 1 }}>
                    <Text style={cp.formLabel}>Duration in months</Text>
                    <TextInput style={cp.formInput} value={form.durationInMonths} onChangeText={pf('durationInMonths')} keyboardType="numeric" placeholder="3" placeholderTextColor="rgba(255,255,255,0.2)" />
                  </View>
                </View>
              )}

              <View style={cp.formRow}>
                <View style={{ flex: 1 }}>
                  <Text style={cp.formLabel}>Max redemptions (optional)</Text>
                  <TextInput style={cp.formInput} value={form.maxRedemptions} onChangeText={pf('maxRedemptions')} keyboardType="numeric" placeholder="e.g. 100" placeholderTextColor="rgba(255,255,255,0.2)" />
                </View>
                <View style={{ flex: 1 }}>
                  <Text style={cp.formLabel}>Expiry date (optional)</Text>
                  <TextInput style={cp.formInput} value={form.redeemBy} onChangeText={pf('redeemBy')} placeholder="YYYY-MM-DD" placeholderTextColor="rgba(255,255,255,0.2)" />
                </View>
              </View>

              {createError && (
                <Text style={{ color: '#EF4444', fontSize: 12, marginBottom: 8 }}>{createError}</Text>
              )}

              <TouchableOpacity style={cp.submitBtn} onPress={handleCreate} disabled={creating} activeOpacity={0.8}>
                {creating
                  ? <ActivityIndicator size="small" color="#fff" />
                  : <><Ionicons name="checkmark" size={15} color={uiVar('adm-ink', '#fff')} /><Text style={cp.submitBtnText}>Create Coupon</Text></>
                }
              </TouchableOpacity>
            </View>
          )}

          {/* Coupons list */}
          {loading && (
            <View style={{ alignItems: 'center', paddingVertical: 20 }}>
              <ActivityIndicator size="small" color="#EC4899" />
            </View>
          )}
          {error && (
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, padding: 12, backgroundColor: 'rgba(239,68,68,0.08)', borderRadius: 8 }}>
              <Ionicons name="alert-circle" size={14} color="#EF4444" />
              <Text style={{ color: '#EF4444', fontSize: 12, flex: 1 }}>{error}</Text>
              <TouchableOpacity onPress={load}><Text style={{ color: '#EF4444', fontSize: 12, fontWeight: '700' }}>Retry</Text></TouchableOpacity>
            </View>
          )}
          {!loading && !error && coupons && (
            coupons.length === 0 ? (
              <Text style={{ color: 'rgba(255,255,255,0.3)', fontSize: 12, textAlign: 'center', paddingVertical: 20 }}>
                No coupons yet. Create one above.
              </Text>
            ) : (
              <View>
                {/* Table header */}
                <View style={cp.tableHeader}>
                  <Text style={[cp.tableHeaderCell, { flex: 1.5 }]}>ID / Name</Text>
                  <Text style={cp.tableHeaderCell}>Discount</Text>
                  <Text style={cp.tableHeaderCell}>Duration</Text>
                  <Text style={cp.tableHeaderCell}>Redeemed</Text>
                  <Text style={cp.tableHeaderCell}>Status</Text>
                  <View style={{ width: 32 }} />
                </View>
                {coupons.map(c => (
                  <View key={c.id} style={[cp.tableRow, !c.valid && cp.tableRowInvalid]}>
                    <View style={{ flex: 1.5 }}>
                      <Text style={cp.couponId}>{c.id}</Text>
                      {c.name && <Text style={cp.couponName}>{c.name}</Text>}
                    </View>
                    <Text style={cp.tableCell}>{formatDiscount(c)}</Text>
                    <Text style={cp.tableCell}>{formatDuration(c)}</Text>
                    <Text style={cp.tableCell}>
                      {c.times_redeemed}{c.max_redemptions ? `/${c.max_redemptions}` : ''}
                    </Text>
                    <View style={{ flex: 1, alignItems: 'flex-start' }}>
                      <View style={[cp.statusPill, c.valid ? cp.statusPillValid : cp.statusPillInvalid]}>
                        <Text style={[cp.statusPillText, c.valid ? { color: '#10B981' } : { color: uiVar('adm-muted2', '#6B7280') }]}>
                          {c.valid ? 'Active' : 'Expired'}
                        </Text>
                      </View>
                    </View>
                    <TouchableOpacity
                      style={cp.archiveBtn}
                      onPress={() => handleArchive(c.id)}
                      disabled={archiving === c.id}
                    >
                      {archiving === c.id
                        ? <ActivityIndicator size="small" color="#EF4444" />
                        : <Ionicons name="trash-outline" size={14} color="rgba(239,68,68,0.6)" />
                      }
                    </TouchableOpacity>
                  </View>
                ))}
              </View>
            )
          )}
        </View>
      )}
    </View>
  );
}

const cp = {
  card: { backgroundColor: 'rgba(236,72,153,0.06)', borderWidth: 1, borderColor: 'rgba(236,72,153,0.2)', borderRadius: 12, padding: 16, marginBottom: 24 },
  headerRow: { flexDirection: 'row', alignItems: 'center', marginBottom: 8 },
  title: { fontSize: 14, fontWeight: '700', color: '#EC4899' },
  countPill: { backgroundColor: 'rgba(236,72,153,0.15)', borderRadius: 5, paddingHorizontal: 7, paddingVertical: 2 },
  countPillText: { fontSize: 10, color: '#EC4899', fontWeight: '800', letterSpacing: 0.5 },
  desc: { fontSize: 12, color: 'rgba(255,255,255,0.45)', marginBottom: 14, lineHeight: 18 },

  refreshBtn: { flexDirection: 'row', alignItems: 'center', gap: 5, paddingHorizontal: 10, paddingVertical: 6, borderRadius: 7, borderWidth: 1, borderColor: 'rgba(255,255,255,0.1)', backgroundColor: 'rgba(255,255,255,0.04)' },
  refreshBtnText: { fontSize: 11, color: 'rgba(255,255,255,0.45)', fontWeight: '600' },
  createBtn: { flexDirection: 'row', alignItems: 'center', gap: 6, paddingHorizontal: 14, paddingVertical: 7, borderRadius: 8, borderWidth: 1, borderColor: 'rgba(236,72,153,0.3)', backgroundColor: 'rgba(236,72,153,0.08)' },
  createBtnText: { fontSize: 12, color: '#EC4899', fontWeight: '700' },

  createForm: { backgroundColor: 'rgba(255,255,255,0.03)', borderRadius: 10, borderWidth: 1, borderColor: 'rgba(255,255,255,0.08)', padding: 14, marginBottom: 16 },
  formTitle: { color: '#fff', fontSize: 13, fontWeight: '700', marginBottom: 12 },
  formRow: { flexDirection: 'row', gap: 12, marginBottom: 12 },
  formLabel: { color: 'rgba(255,255,255,0.5)', fontSize: 10, fontWeight: '700', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4 },
  formInput: { backgroundColor: 'rgba(255,255,255,0.06)', borderRadius: 8, borderWidth: 1, borderColor: 'rgba(255,255,255,0.1)', paddingHorizontal: 10, paddingVertical: 7, color: '#fff', fontSize: 12 },
  typeBtn: { flex: 1, paddingVertical: 7, paddingHorizontal: 10, borderRadius: 7, borderWidth: 1, borderColor: 'rgba(255,255,255,0.1)', backgroundColor: 'rgba(255,255,255,0.04)', alignItems: 'center' },
  typeBtnActive: { borderColor: '#EC4899', backgroundColor: 'rgba(236,72,153,0.12)' },
  typeBtnText: { fontSize: 12, color: 'rgba(255,255,255,0.5)', fontWeight: '600' },
  typeBtnTextActive: { color: '#EC4899' },
  typeHint: { fontSize: 9, color: 'rgba(255,255,255,0.25)', marginTop: 2, textAlign: 'center' },
  submitBtn: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 7, backgroundColor: '#EC4899', paddingVertical: 10, borderRadius: 9, marginTop: 4 },
  submitBtnText: { color: '#fff', fontWeight: '700', fontSize: 13 },

  tableHeader: { flexDirection: 'row', alignItems: 'center', paddingVertical: 6, paddingHorizontal: 8, borderBottomWidth: 1, borderBottomColor: 'rgba(255,255,255,0.06)', marginBottom: 2 },
  tableHeaderCell: { flex: 1, fontSize: 9, color: 'rgba(255,255,255,0.3)', fontWeight: '700', textTransform: 'uppercase', letterSpacing: 0.5 },
  tableRow: { flexDirection: 'row', alignItems: 'center', paddingVertical: 8, paddingHorizontal: 8, borderRadius: 8, marginBottom: 2, backgroundColor: 'rgba(255,255,255,0.02)' },
  tableRowInvalid: { opacity: 0.5 },
  couponId: { color: 'rgba(255,255,255,0.85)', fontSize: 12, fontWeight: '700', fontFamily: Platform.OS === 'web' ? 'monospace' : 'Courier' },
  couponName: { color: 'rgba(255,255,255,0.4)', fontSize: 10, marginTop: 2 },
  tableCell: { flex: 1, color: 'rgba(255,255,255,0.65)', fontSize: 12 },
  statusPill: { paddingHorizontal: 7, paddingVertical: 3, borderRadius: 5 },
  statusPillValid: { backgroundColor: 'rgba(16,185,129,0.12)' },
  statusPillInvalid: { backgroundColor: 'rgba(107,114,128,0.12)' },
  statusPillText: { fontSize: 10, fontWeight: '700' },
  archiveBtn: { width: 32, height: 32, justifyContent: 'center', alignItems: 'center', borderRadius: 7, backgroundColor: 'rgba(239,68,68,0.06)' },
};

// ─────────────────────────────────────────────────────────────────────────────

export default function FinancesTab() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [allExpanded, setAllExpanded] = useState(false);
  const [expandedUserType, setExpandedUserType] = useState(null);
  const [editingFeature, setEditingFeature] = useState(null);
  const [newFeatureText, setNewFeatureText] = useState({});
  const [usingDefaults, setUsingDefaults] = useState(false);

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    setError(null);
    setUsingDefaults(false);
    try {
      const base = getBase();
      if (!base) throw new Error('No functions URL configured');
      const res = await fetch(`${base}/getSubscriptionConfig`, { headers: await getAdminAuthHeaders() });
      const json = await res.json();
      if (json.success) {
        setConfig(json.data);
      } else {
        throw new Error(json.error || 'Failed to load config');
      }
    } catch (e) {
      console.warn('[FinancesTab] Cloud function unavailable, using local defaults:', e.message);
      setConfig({ ...LOCAL_DEFAULTS });
      setUsingDefaults(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchConfig(); }, [fetchConfig]);

  const handleSave = async () => {
    if (!config || usingDefaults) return;
    setSaving(true);
    setSaveSuccess(false);
    setError(null);
    try {
      const base = getBase();
      const res = await fetch(`${base}/updateSubscriptionConfig`, {
        method: 'POST',
        headers: await getAdminAuthHeaders(),
        body: JSON.stringify({ plans: config.plans, userTypes: config.userTypes }),
      });
      const json = await res.json();
      if (json.success) {
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
      } else {
        setError(json.error || 'Save failed');
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const updatePlan = (planKey, field, value) => {
    setConfig(prev => ({
      ...prev,
      plans: {
        ...prev.plans,
        [planKey]: { ...prev.plans[planKey], [field]: value },
      },
    }));
  };

  const updatePrice = (planKey, interval, amount) => {
    setConfig(prev => {
      const plan = prev.plans[planKey] || {};
      const prices = plan.prices || {};
      return { ...prev, plans: { ...prev.plans, [planKey]: { ...plan, prices: { ...prices, [interval]: { ...(prices[interval] || {}), amount } } } } };
    });
  };

  const updateUserType = (typeKey, field, value) => {
    setConfig(prev => ({
      ...prev,
      userTypes: {
        ...prev.userTypes,
        [typeKey]: { ...prev.userTypes[typeKey], [field]: value },
      },
    }));
  };

  const addFeature = (planKey) => {
    const text = (newFeatureText[planKey] || '').trim();
    if (!text) return;
    const features = [...(config.plans[planKey].features || []), text];
    updatePlan(planKey, 'features', features);
    setNewFeatureText(prev => ({ ...prev, [planKey]: '' }));
  };

  const removeFeature = (planKey, index) => {
    const features = [...(config.plans[planKey].features || [])];
    features.splice(index, 1);
    updatePlan(planKey, 'features', features);
  };

  const updateFeature = (planKey, index, text) => {
    const features = [...(config.plans[planKey].features || [])];
    features[index] = text;
    updatePlan(planKey, 'features', features);
  };

  if (loading) {
    return (
      <View style={s.center}>
        <ActivityIndicator size="large" color="#A855F7" />
        <Text style={s.loadingText}>Loading subscription config...</Text>
      </View>
    );
  }

  if (error && !config) {
    return (
      <View style={s.center}>
        <Ionicons name="alert-circle" size={48} color="#EF4444" />
        <Text style={s.errorText}>{error}</Text>
        <TouchableOpacity style={s.retryBtn} onPress={fetchConfig}>
          <Text style={s.retryBtnText}>Retry</Text>
        </TouchableOpacity>
      </View>
    );
  }

  const planOrder = ['FREE', 'BASIC', 'PRO', 'ULTIMATE'];
  const plans = config?.plans || {};
  const userTypes = config?.userTypes || {};

  return (
    <ScrollView style={s.container} contentContainerStyle={s.content}>
      <StripeModeSection />
      <StripeCatalogSection onApplied={fetchConfig} />
      <CouponsSection />
      <PricingExperimentsSection />

      {/* Defaults banner */}
      {usingDefaults && (
        <View style={s.defaultsBanner}>
          <Ionicons name="information-circle" size={18} color="#F59E0B" />
          <Text style={s.defaultsBannerText}>
            Couldn't load the saved config (deploy functions, or sign in as an admin). Showing defaults read-only; saving is off.
          </Text>
        </View>
      )}

      {/* Header */}
      <View style={s.header}>
        <View>
          <Text style={s.title}>Subscription Plans & Limits</Text>
          <Text style={s.subtitle}>
            Prices, search limits (per month or per day), background searches and the features shown on the Plans page.
            {usingDefaults ? ' Saving is off until the config loads.' : ' Saves update the Plans page at once and quota limits within a minute. Price changes need "Create live prices" above.'}
          </Text>
        </View>
        <View style={{ flexDirection: 'row', gap: 10, alignItems: 'center' }}>
          {saveSuccess && (
            <View style={s.successBadge}>
              <Ionicons name="checkmark-circle" size={16} color="#10B981" />
              <Text style={{ color: '#10B981', fontSize: 13, fontWeight: '600' }}>Saved</Text>
            </View>
          )}
          {error && config && (
            <Text style={{ color: '#EF4444', fontSize: 12 }}>{error}</Text>
          )}
          <TouchableOpacity style={[s.saveBtn, usingDefaults && { opacity: 0.4 }]} onPress={handleSave} disabled={saving || usingDefaults}>
            {saving ? (
              <ActivityIndicator size="small" color="#fff" />
            ) : (
              <>
                <Ionicons name="cloud-upload-outline" size={16} color={uiVar('adm-ink', '#fff')} />
                <Text style={s.saveBtnText}>Save All Changes</Text>
              </>
            )}
          </TouchableOpacity>
        </View>
      </View>

      {/* Plan Cards Header */}
      <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <Text style={s.sectionTitle}>Plan Configuration</Text>
        <TouchableOpacity
          style={s.expandAllBtn}
          onPress={() => setAllExpanded(e => !e)}
          activeOpacity={0.7}
        >
          <Ionicons name={allExpanded ? 'contract-outline' : 'expand-outline'} size={14} color={uiVar('adm-accent', '#A855F7')} />
          <Text style={s.expandAllBtnText}>{allExpanded ? 'Collapse All' : 'Expand All'}</Text>
        </TouchableOpacity>
      </View>

      {/* Horizontal Plan Cards */}
      <View style={s.planCardsRow}>
        {planOrder.map(key => {
          const plan = plans[key];
          if (!plan) return null;
          const colors = PLAN_COLORS[key];

          return (
            <View key={key} style={[s.planCard, { borderColor: colors.border }]}>
              {/* Card Header — always visible */}
              <View style={[s.planCardHead, { borderBottomColor: colors.accent + '30' }]}>
                <View style={[s.planBadge, { backgroundColor: colors.accent + '20' }]}>
                  <Text style={[s.planBadgeText, { color: colors.accent }]}>{key}</Text>
                </View>
                <Text style={s.planCardPrice}>
                  {!(plan.prices?.month?.amount > 0) ? 'Free' : formatPrice(plan.prices.month.amount)}
                  {plan.prices?.month?.amount > 0 && <Text style={s.planCardPriceSub}>/mo</Text>}
                </Text>
                {plan.prices?.year?.amount > 0 && (
                  <Text style={s.planCardMeta}>{formatPrice(plan.prices.year.amount)}/yr · saves {yearlySavings(plan).pct}%</Text>
                )}
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginTop: 6 }}>
                  <View style={[s.activeDot, { backgroundColor: plan.active ? '#10B981' : uiVar('adm-raised', '#4B5563') }]} />
                  <Text style={s.planCardMeta}>
                    {plan.queryLimit === -1 ? '∞' : plan.queryLimit} searches / {plan.queryPeriod === 'day' ? 'day' : 'month'}
                  </Text>
                </View>
                <Text style={s.planCardMeta}>{plan.backgroundQueryLimit} background · {['month', 'year'].map(iv => plan.prices?.[iv]?.live ? `${iv} live ✓` : null).filter(Boolean).join(' · ') || (key === 'FREE' ? 'no billing' : 'no live price')}</Text>
              </View>

              {/* Expanded details */}
              {allExpanded && (
                <View style={s.planCardBody}>
                  <FieldGroup label="Display Name">
                    <TextInput style={s.textInput} value={plan.name}
                      onChangeText={v => updatePlan(key, 'name', v)} />
                  </FieldGroup>
                  <FieldGroup label="Tagline">
                    <TextInput style={s.textInput} value={plan.tagline || ''}
                      onChangeText={v => updatePlan(key, 'tagline', v)} />
                  </FieldGroup>
                  {key !== 'FREE' && (
                    <>
                      <FieldGroup label="Monthly price ($)">
                        <NumericInput value={plan.prices?.month?.amount ?? 0} onChange={v => updatePrice(key, 'month', v)} step={1} min={0} />
                      </FieldGroup>
                      <FieldGroup label="Yearly price ($)" hint={plan.prices?.year?.amount ? `${yearlySavings(plan).pct}% off` : undefined}>
                        <NumericInput value={plan.prices?.year?.amount ?? 0} onChange={v => updatePrice(key, 'year', v)} step={10} min={0} />
                      </FieldGroup>
                      <FieldGroup label="Featured card">
                        <TouchableOpacity style={[s.toggleBtn, plan.highlight && s.toggleBtnOn]} onPress={() => updatePlan(key, 'highlight', !plan.highlight)}>
                          <Text style={{ color: plan.highlight ? '#10B981' : 'rgba(255,255,255,0.5)', fontWeight: '700', fontSize: 12 }}>{plan.highlight ? 'YES' : 'NO'}</Text>
                        </TouchableOpacity>
                      </FieldGroup>
                    </>
                  )}
                  <FieldGroup label="Active">
                    <TouchableOpacity style={[s.toggleBtn, plan.active && s.toggleBtnOn]}
                      onPress={() => updatePlan(key, 'active', !plan.active)}>
                      <Text style={{ color: plan.active ? '#10B981' : '#EF4444', fontWeight: '700', fontSize: 12 }}>
                        {plan.active ? 'ON' : 'OFF'}
                      </Text>
                    </TouchableOpacity>
                  </FieldGroup>

                  <View style={s.planCardDivider} />
                  <Text style={s.planCardSectionLabel}>Limits</Text>

                  <FieldGroup label="Searches" hint="-1 = ∞">
                    <NumericInput value={plan.queryLimit} onChange={v => updatePlan(key, 'queryLimit', v)} step={1} allowNegative />
                  </FieldGroup>
                  <FieldGroup label="Per">
                    <View style={{ flexDirection: 'row', gap: 6 }}>
                      {['month', 'day'].map(pd => (
                        <TouchableOpacity key={pd} style={[s.toggleBtn, (plan.queryPeriod || 'month') === pd && s.toggleBtnOn]} onPress={() => updatePlan(key, 'queryPeriod', pd)} accessibilityRole="button" accessibilityState={{ selected: (plan.queryPeriod || 'month') === pd }}>
                          <Text style={{ color: (plan.queryPeriod || 'month') === pd ? '#10B981' : 'rgba(255,255,255,0.5)', fontWeight: '700', fontSize: 12 }}>{pd.toUpperCase()}</Text>
                        </TouchableOpacity>
                      ))}
                    </View>
                  </FieldGroup>
                  <FieldGroup label="BG Queries">
                    <NumericInput value={plan.backgroundQueryLimit} onChange={v => updatePlan(key, 'backgroundQueryLimit', v)} step={1} min={0} max={20} />
                  </FieldGroup>
                  <View style={s.planCardDivider} />
                  <Text style={s.planCardSectionLabel}>Stripe</Text>

                  {['month', 'year'].map(iv => (
                    <FieldGroup key={iv} label={`${iv === 'year' ? 'Yearly' : 'Monthly'} price ids`}>
                      <Text selectable style={[s.featureText, { fontSize: 10 }]}>live {plan.prices?.[iv]?.live || '—'}</Text>
                      <Text selectable style={[s.featureText, { fontSize: 10 }]}>test {plan.prices?.[iv]?.test || '—'}</Text>
                    </FieldGroup>
                  ))}

                  <View style={s.planCardDivider} />
                  <Text style={s.planCardSectionLabel}>Features ({(plan.features || []).length})</Text>

                  {(plan.features || []).map((feat, i) => (
                    <View key={i} style={s.featureRow}>
                      <Ionicons name="checkmark-circle" size={12} color={colors.accent} style={{ marginTop: 2 }} />
                      {editingFeature?.planKey === key && editingFeature?.index === i ? (
                        <TextInput style={[s.textInput, { flex: 1, fontSize: 11 }]} value={feat}
                          onChangeText={v => updateFeature(key, i, v)}
                          onBlur={() => setEditingFeature(null)} autoFocus />
                      ) : (
                        <TouchableOpacity style={{ flex: 1 }}
                          onPress={() => setEditingFeature({ planKey: key, index: i })}>
                          <Text style={s.featureText}>{feat}</Text>
                        </TouchableOpacity>
                      )}
                      <TouchableOpacity onPress={() => removeFeature(key, i)}>
                        <Ionicons name="close-circle" size={14} color="rgba(239,68,68,0.5)" />
                      </TouchableOpacity>
                    </View>
                  ))}
                  <View style={s.addFeatureRow}>
                    <TextInput style={[s.textInput, { flex: 1, fontSize: 11 }]}
                      value={newFeatureText[key] || ''}
                      onChangeText={v => setNewFeatureText(prev => ({ ...prev, [key]: v }))}
                      placeholder="Add feature..." placeholderTextColor="rgba(255,255,255,0.2)"
                      onSubmitEditing={() => addFeature(key)} />
                    <TouchableOpacity style={[s.addFeatureBtn, { backgroundColor: colors.accent + '20' }]}
                      onPress={() => addFeature(key)}>
                      <Ionicons name="add" size={14} color={colors.accent} />
                    </TouchableOpacity>
                  </View>
                </View>
              )}
            </View>
          );
        })}
      </View>

      {/* User Types */}
      <Text style={[s.sectionTitle, { marginTop: 32 }]}>User Type Overrides</Text>
      <Text style={s.sectionSubtitle}>
        Configure limits per user type. "User" inherits from their subscription plan. "Admin" gets custom overrides.
      </Text>

      {Object.entries(userTypes).map(([typeKey, typeConfig]) => {
        const colors = USER_TYPE_COLORS[typeKey] || USER_TYPE_COLORS.user;
        const isExpanded = expandedUserType === typeKey;

        return (
          <View key={typeKey} style={[s.card, { borderColor: colors.border }]}>
            <TouchableOpacity
              style={s.cardHeader}
              onPress={() => setExpandedUserType(isExpanded ? null : typeKey)}
              activeOpacity={0.7}
            >
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
                <Ionicons
                  name={typeKey === 'admin' ? 'shield-checkmark' : 'person'}
                  size={18}
                  color={colors.accent}
                />
                <Text style={s.cardTitle}>{typeConfig.name}</Text>
                <Text style={s.cardSubtitle}>{typeConfig.description}</Text>
              </View>
              <Ionicons
                name={isExpanded ? 'chevron-up' : 'chevron-down'}
                size={18}
                color="rgba(255,255,255,0.4)"
              />
            </TouchableOpacity>

            {isExpanded && (
              <View style={s.cardBody}>
                <View style={s.fieldRow}>
                  <FieldGroup label="Display Name" width="30%">
                    <TextInput
                      style={s.textInput}
                      value={typeConfig.name}
                      onChangeText={v => updateUserType(typeKey, 'name', v)}
                    />
                  </FieldGroup>
                  <FieldGroup label="Description" width="45%">
                    <TextInput
                      style={s.textInput}
                      value={typeConfig.description || ''}
                      onChangeText={v => updateUserType(typeKey, 'description', v)}
                    />
                  </FieldGroup>
                  {typeConfig.inheritsFromPlan ? (
                    <FieldGroup label="Limits" width="20%">
                      <View style={s.readOnlyField}>
                        <Text style={[s.readOnlyText, { color: '#10B981' }]}>From Plan</Text>
                      </View>
                    </FieldGroup>
                  ) : (
                    <>
                      <FieldGroup label="Query Limit" width="12%" hint="-1 = ∞">
                        <NumericInput
                          value={typeConfig.queryLimit}
                          onChange={v => updateUserType(typeKey, 'queryLimit', v)}
                          step={1}
                          allowNegative
                        />
                      </FieldGroup>
                      <FieldGroup label="BG Query Limit" width="12%">
                        <NumericInput
                          value={typeConfig.backgroundQueryLimit}
                          onChange={v => updateUserType(typeKey, 'backgroundQueryLimit', v)}
                          step={1}
                          min={0}
                        />
                      </FieldGroup>
                    </>
                  )}
                </View>
              </View>
            )}
          </View>
        );
      })}

      {/* Metadata footer */}
      {config?.updatedAt && (
        <Text style={s.metaFooter}>
          Last updated: {new Date(config.updatedAt).toLocaleString()}
        </Text>
      )}

      <View style={{ height: 60 }} />
    </ScrollView>
  );
}

/* ---------- Sub-components ---------- */

function FieldGroup({ label, hint, width, children }) {
  return (
    <View style={width ? { width, minWidth: 100 } : undefined}>
      <Text style={s.fieldLabel}>{label}</Text>
      {hint && <Text style={s.fieldHint}>{hint}</Text>}
      {children}
    </View>
  );
}

function NumericInput({ value, onChange, step = 1, min, max, allowNegative = false }) {
  const display = value === -1 ? '∞' : String(value ?? 0);
  const isUnlimited = value === -1;

  const increment = () => {
    if (isUnlimited) return;
    const next = (value ?? 0) + step;
    if (max !== undefined && next > max) return;
    onChange(Math.round(next * 100) / 100);
  };

  const decrement = () => {
    const next = isUnlimited ? 999 : (value ?? 0) - step;
    if (allowNegative && next < -1) return;
    if (!allowNegative && min !== undefined && next < min) return;
    onChange(Math.round(next * 100) / 100);
  };

  const toggleUnlimited = () => {
    if (!allowNegative) return;
    onChange(isUnlimited ? 10 : -1);
  };

  return (
    <View style={s.numericWrap}>
      <TouchableOpacity style={s.numBtn} onPress={decrement}>
        <Ionicons name="remove" size={14} color="rgba(255,255,255,0.6)" />
      </TouchableOpacity>
      <TouchableOpacity
        style={s.numDisplay}
        onPress={allowNegative ? toggleUnlimited : undefined}
        activeOpacity={allowNegative ? 0.6 : 1}
      >
        <Text style={[s.numText, isUnlimited && { color: '#F59E0B', fontSize: 18 }]}>
          {display}
        </Text>
      </TouchableOpacity>
      <TouchableOpacity style={s.numBtn} onPress={increment}>
        <Ionicons name="add" size={14} color="rgba(255,255,255,0.6)" />
      </TouchableOpacity>
    </View>
  );
}

/* ---------- Styles ---------- */

const s = {
  container: { flex: 1 },
  content: { padding: 24, maxWidth: 1100 },
  center: { flex: 1, justifyContent: 'center', alignItems: 'center', padding: 40 },
  loadingText: { color: 'rgba(255,255,255,0.5)', marginTop: 12, fontSize: 14 },
  errorText: { color: '#EF4444', marginTop: 12, fontSize: 14, textAlign: 'center' },
  retryBtn: { marginTop: 16, backgroundColor: uiVar('adm-accent-wash-20', 'rgba(168,85,247,0.2)'), paddingHorizontal: 20, paddingVertical: 10, borderRadius: 8 },
  retryBtnText: { color: uiVar('adm-accent', '#A855F7'), fontWeight: '600' },

  header: {
    flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start',
    flexWrap: 'wrap', gap: 16, marginBottom: 24,
  },
  title: { color: uiVar('adm-ink', '#fff'), fontSize: 22, fontWeight: '700' },
  subtitle: { color: 'rgba(255,255,255,0.45)', fontSize: 13, marginTop: 4, maxWidth: 500 },

  saveBtn: {
    flexDirection: 'row', alignItems: 'center', gap: 8,
    backgroundColor: uiVar('adm-accent-fill', '#A855F7'), paddingHorizontal: 18, paddingVertical: 10, borderRadius: 10,
  },
  saveBtnText: { color: uiVar('adm-ink', '#fff'), fontWeight: '700', fontSize: 13 },
  successBadge: {
    flexDirection: 'row', alignItems: 'center', gap: 4,
    backgroundColor: 'rgba(16,185,129,0.1)', paddingHorizontal: 12, paddingVertical: 6, borderRadius: 8,
  },

  expandAllBtn: {
    flexDirection: 'row', alignItems: 'center', gap: 6,
    backgroundColor: uiVar('adm-accent-wash-10', 'rgba(168,85,247,0.1)'), paddingHorizontal: 14, paddingVertical: 7, borderRadius: 8,
    borderWidth: 1, borderColor: uiVar('adm-accent-wash-20', 'rgba(168,85,247,0.2)'),
  },
  expandAllBtnText: { color: uiVar('adm-accent', '#A855F7'), fontSize: 12, fontWeight: '600' },

  planCardsRow: { flexDirection: 'row', gap: 12, marginBottom: 28, alignItems: 'flex-start' },
  planCard: {
    flex: 1, minWidth: 200, backgroundColor: 'rgba(255,255,255,0.03)',
    borderWidth: 1, borderRadius: 14, overflow: 'hidden',
  },
  planCardHead: {
    padding: 16, alignItems: 'center', borderBottomWidth: 1,
  },
  planCardPrice: { color: uiVar('adm-ink', '#fff'), fontSize: 28, fontWeight: '800', marginTop: 10 },
  planCardPriceSub: { fontSize: 14, fontWeight: '500', color: 'rgba(255,255,255,0.4)' },
  planCardMeta: { color: 'rgba(255,255,255,0.35)', fontSize: 11, marginTop: 2 },
  planCardBody: { padding: 14, gap: 8 },
  planCardDivider: { height: 1, backgroundColor: 'rgba(255,255,255,0.06)', marginVertical: 4 },
  planCardSectionLabel: { color: 'rgba(255,255,255,0.4)', fontSize: 10, fontWeight: '700', textTransform: 'uppercase', letterSpacing: 0.5 },

  sectionTitle: { color: uiVar('adm-ink', '#fff'), fontSize: 16, fontWeight: '700', marginBottom: 0 },
  sectionSubtitle: { color: 'rgba(255,255,255,0.4)', fontSize: 12, marginBottom: 16, marginTop: -8 },

  card: {
    backgroundColor: 'rgba(255,255,255,0.03)', borderWidth: 1, borderRadius: 14,
    marginBottom: 12, overflow: 'hidden',
  },
  cardHeader: {
    flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center',
    padding: 14, paddingHorizontal: 18,
  },
  cardTitle: { color: uiVar('adm-ink', '#fff'), fontSize: 15, fontWeight: '700' },
  cardPrice: { color: 'rgba(255,255,255,0.5)', fontSize: 13 },
  cardSubtitle: { color: 'rgba(255,255,255,0.35)', fontSize: 12 },
  planBadge: { paddingHorizontal: 8, paddingVertical: 3, borderRadius: 6 },
  planBadgeText: { fontSize: 10, fontWeight: '800', letterSpacing: 0.5 },
  activeDot: { width: 8, height: 8, borderRadius: 4 },

  cardBody: {
    padding: 18, paddingTop: 8,
    borderTopWidth: 1, borderTopColor: 'rgba(255,255,255,0.05)',
  },
  fieldRow: { flexDirection: 'row', gap: 14, marginBottom: 14, flexWrap: 'wrap' },
  fieldLabel: { color: 'rgba(255,255,255,0.5)', fontSize: 11, fontWeight: '600', marginBottom: 4 },
  fieldHint: { color: 'rgba(255,255,255,0.25)', fontSize: 9, marginBottom: 2 },

  textInput: {
    backgroundColor: 'rgba(255,255,255,0.06)', borderRadius: 8, borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.08)', paddingHorizontal: 10, paddingVertical: 7,
    color: uiVar('adm-ink', '#fff'), fontSize: 13,
  },
  readOnlyField: {
    backgroundColor: 'rgba(255,255,255,0.03)', borderRadius: 8, paddingHorizontal: 10,
    paddingVertical: 8, borderWidth: 1, borderColor: 'rgba(255,255,255,0.05)',
  },
  readOnlyText: { color: 'rgba(255,255,255,0.4)', fontSize: 13 },

  toggleBtn: {
    backgroundColor: 'rgba(239,68,68,0.1)', borderRadius: 8, paddingHorizontal: 14,
    paddingVertical: 7, borderWidth: 1, borderColor: 'rgba(239,68,68,0.2)', alignItems: 'center',
  },
  toggleBtnOn: {
    backgroundColor: 'rgba(16,185,129,0.1)', borderColor: 'rgba(16,185,129,0.2)',
  },

  numericWrap: {
    flexDirection: 'row', alignItems: 'center', backgroundColor: 'rgba(255,255,255,0.06)',
    borderRadius: 8, borderWidth: 1, borderColor: 'rgba(255,255,255,0.08)', overflow: 'hidden',
  },
  numBtn: {
    paddingHorizontal: 10, paddingVertical: 8, justifyContent: 'center', alignItems: 'center',
  },
  numDisplay: { flex: 1, alignItems: 'center', paddingVertical: 6 },
  numText: { color: uiVar('adm-ink', '#fff'), fontSize: 14, fontWeight: '700' },

  featuresSection: { marginTop: 4 },
  featuresLabel: { color: 'rgba(255,255,255,0.5)', fontSize: 11, fontWeight: '600', marginBottom: 8 },
  featureRow: {
    flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 4,
    paddingVertical: 4, paddingHorizontal: 6, borderRadius: 6,
  },
  featureText: { color: 'rgba(255,255,255,0.7)', fontSize: 12, flex: 1 },
  addFeatureRow: { flexDirection: 'row', gap: 8, marginTop: 6 },
  addFeatureBtn: { borderRadius: 8, padding: 8, justifyContent: 'center', alignItems: 'center' },

  defaultsBanner: {
    flexDirection: 'row', alignItems: 'center', gap: 10,
    backgroundColor: 'rgba(245,158,11,0.08)', borderWidth: 1, borderColor: 'rgba(245,158,11,0.2)',
    borderRadius: 10, padding: 12, marginBottom: 16,
  },
  defaultsBannerText: { color: '#F59E0B', fontSize: 12, flex: 1 },

  metaFooter: {
    color: 'rgba(255,255,255,0.2)', fontSize: 11, textAlign: 'center', marginTop: 24,
  },
};
